import React from 'react';
import {
  GetApp,
  KeyboardArrowDown,
  KeyboardArrowUp,
  Refresh,
  Search,
  ViewColumn
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  Menu,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useMicrospecDynamicUIContext } from '../../GlobalProvider';
import { getPaginatedData } from '../../Helpers/dataUtils';
import { getLtrOrRtlByAlignment, getRightOrLeft, getRtlOrLtr, isRTL } from '../../Helpers/Utils';
import { useMicrospecThemeLanguageContext } from '../../Themes';

/**
 * Column definition for MicrospecDataTable
 */
export interface MicrospecDataTableColumn {
  field: string;
  headerName: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  minWidth?: number;
  maxWidth?: number;
  render?: (value: any, row: any) => React.ReactNode;
  expandable?: boolean;
  expandableRender?: (row: any) => React.ReactNode;
  sx?: any;
  headerSx?: any;
}

/**
 * Row action definition
 */
export interface MicrospecDataTableAction {
  icon: React.ReactNode | ((row: any) => React.ReactNode);
  label: string | ((row: any) => string);
  onClick: (row: any) => void;
  disabled?: (row: any) => boolean;
}

/**
 * Global action definition
 */
export interface MicrospecDataTableGlobalAction {
  icon?: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  variant?: 'text' | 'outlined' | 'contained';
  color?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
  sx?: any;
}

/**
 * Where clause for filtering
 */
export interface MicrospecDataTableWhere {
  field: string;
  operator: string;
  value: any;
}

/**
 * Order by configuration
 */
export interface MicrospecDataTableOrderBy {
  field: string;
  direction: 'asc' | 'desc';
}

/**
 * Props for MicrospecDataTable component
 */
export interface MicrospecDataTableProps {
  // Core data props
  collection: string;
  columns: MicrospecDataTableColumn[];
  wheres?: any[];
  orderBy?: MicrospecDataTableOrderBy;

  // Event handlers
  onRowClick?: (row: any) => void;
  onRowSelect?: (selectedIds: string[], lastSelectedId: string | null) => void;
  onBulkAction?: (selectedRows: any[]) => void;
  onDataLoad?: (data: any[]) => Promise<any[]> | any[];

  // Configuration
  actions?: MicrospecDataTableAction[];
  globalActions?: MicrospecDataTableGlobalAction[];
  selectable?: boolean;
  searchable?: boolean;
  exportable?: boolean;
  refreshable?: boolean;

  // Display
  title?: string;
  subtitle?: string;
  emptyMessage?: string;
  pageSize?: number;
  rowHeight?: number;
  maxHeight?: number;

  // Styling
  sx?: any;
  dense?: boolean;
  stickyHeader?: boolean;

  [key: string]: any;
}

/**
 * Modern, feature-rich DataTable component with pagination
 * 
 * Features:
 * - Firebase Firestore integration with dataUtils
 * - Advanced filtering and sorting
 * - Responsive design with mobile adaptations
 * - Customizable cell renderers
 * - Row selection and bulk actions
 * - Modern Material-UI design
 * - RTL support
 * - Performance optimized
 */
const MicrospecDataTable: React.FC<MicrospecDataTableProps> = ({
  // Core data props
  collection,
  columns = [],
  wheres = [],
  orderBy: orderByConfig,

  // Event handlers
  onRowClick,
  onRowSelect,
  onBulkAction,
  onDataLoad,

  // Configuration
  actions = [],
  globalActions = [],
  selectable = false,
  searchable = true,
  exportable = false,
  refreshable = true,

  // Display
  title,
  subtitle,
  emptyMessage,
  pageSize = 50,
  rowHeight = 60,
  maxHeight = 600,

  // Styling
  sx = {},
  dense = false,
  stickyHeader = true,
  ...otherProps
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const { cloudProvider } = useMicrospecDynamicUIContext();
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const { firestore } = cloudProvider;

  // Use ref to store the latest onDataLoad callback without triggering re-renders
  const onDataLoadRef = useRef(onDataLoad);

  // Update ref when onDataLoad changes
  useEffect(() => {
    onDataLoadRef.current = onDataLoad;
  }, [onDataLoad]);

  // Use ref to store columns to avoid recreating visibleColumns
  const columnsRef = useRef(columns);
  useEffect(() => {
    columnsRef.current = columns;
  }, [columns]);

  // Use ref to store wheres to avoid infinite loops
  const wheresRef = useRef(wheres);
  useEffect(() => {
    wheresRef.current = wheres;
  }, [wheres]);

  // State management
  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortConfig, setSortConfig] = useState<MicrospecDataTableOrderBy | undefined>(orderByConfig);
  const [columnsAnchor, setColumnsAnchor] = useState<null | HTMLElement>(null);
  const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => new Set(columns.map(col => col.field)));
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  // Pagination state
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(pageSize);

  // Export dialog state
  const [exportDialogOpen, setExportDialogOpen] = useState<boolean>(false);
  const [exportFormat, setExportFormat] = useState<'csv' | 'json'>('csv');
  const [exportScope, setExportScope] = useState<'current' | 'range' | 'all'>('current');
  const [exportRangeStart, setExportRangeStart] = useState<number>(1);
  const [exportRangeEnd, setExportRangeEnd] = useState<number>(1);

  // Helper function to get nested field value
  const getNestedValue = useCallback((obj: any, path: string): any => {
    if (!path) return undefined;
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }, []);

  // Memoized filtered and sorted data
  const processedData = useMemo(() => {
    let result = [...data];

    // Apply search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(row => {
        return columnsRef.current.some(column => {
          const value = getNestedValue(row, column.field);
          if (value == null) return false;
          return String(value).toLowerCase().includes(query);
        });
      });
    }

    return result;
  }, [data, searchQuery, getNestedValue]);

  // Load initial data
  const loadData = useCallback(async () => {
    if (!collection || !firestore) return;

    try {
      setLoading(true);
      setData([]);
      setSelectedRows(new Set());
      setError(null);

      // Load a larger dataset for client-side pagination
      const results = await getPaginatedData(
        null,
        1000, // Load more data for client-side pagination
        collection,
        firestore,
        sortConfig?.direction as any,
        sortConfig?.field as any,
        wheresRef.current
      );

      // Apply data transformation if provided
      const processedResults = onDataLoadRef.current ? await onDataLoadRef.current(results) : results;

      setData(processedResults);

    } catch (err: any) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [collection, firestore, sortConfig]);

  // Initial data load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Refresh data
  const refreshData = useCallback(() => {
    setPage(0);
    loadData();
  }, [loadData]);

  // Handle sorting
  const handleSort = useCallback((field: string) => {
    if (!sortConfig) {
      // If no sort config exists, start with ascending
      setSortConfig({ field, direction: 'asc' });
    } else {
      const isCurrentField = sortConfig.field === field;
      const newDirection = isCurrentField && sortConfig.direction === 'asc' ? 'desc' : 'asc';
      setSortConfig({ field, direction: newDirection });
    }
    setPage(0);

    // Reload data with new sort
    setTimeout(() => {
      loadData();
    }, 0);
  }, [sortConfig, loadData]);

  // Handle page change
  const handleChangePage = useCallback((event: unknown, newPage: number) => {
    setPage(newPage);
  }, []);

  // Handle rows per page change
  const handleChangeRowsPerPage = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Handle row selection
  const handleRowSelect = useCallback((rowId: string, selected: boolean) => {
    setSelectedRows(prev => {
      const newSet = new Set(prev);
      if (selected) {
        newSet.add(rowId);
      } else {
        newSet.delete(rowId);
      }

      if (onRowSelect) {
        onRowSelect(Array.from(newSet), newSet.has(rowId) ? rowId : null);
      }

      return newSet;
    });
  }, [onRowSelect]);

  // Handle select all
  const handleSelectAll = useCallback((selected: boolean) => {
    if (selected) {
      const allIds = new Set(processedData.map(row => row.id));
      setSelectedRows(allIds);
      if (onRowSelect) {
        onRowSelect(Array.from(allIds), null);
      }
    } else {
      setSelectedRows(new Set());
      if (onRowSelect) {
        onRowSelect([], null);
      }
    }
  }, [processedData, onRowSelect]);

  // Handle row expansion
  const handleRowExpand = useCallback((rowId: string) => {
    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
      } else {
        newSet.add(rowId);
      }
      return newSet;
    });
  }, []);

  // Handle column visibility
  const handleColumnVisibility = useCallback((field: string, visible: boolean) => {
    setVisibleColumns(prev => {
      const newSet = new Set(prev);
      if (visible) {
        newSet.add(field);
      } else {
        newSet.delete(field);
      }
      return newSet;
    });
  }, []);

  // Get visible columns
  const visibleColumnsData = useMemo(() => {
    return columnsRef.current.filter(col => visibleColumns.has(col.field));
  }, [visibleColumns]);

  // Get paginated data
  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return processedData.slice(startIndex, endIndex);
  }, [processedData, page, rowsPerPage]);

  // Memoize translated strings to prevent re-renders
  const translatedStrings = useMemo(() => ({
    search: localizationProvider.translate({ type: 'Internal', text: 'search' }),
    selected: localizationProvider.translate({ type: 'Internal', text: 'selected' }),
    items: localizationProvider.translate({ type: 'Internal', text: 'items' }),
    refresh: localizationProvider.translate({ type: 'Internal', text: 'refresh' }),
    export: localizationProvider.translate({ type: 'Internal', text: 'export' }),
    columnSettings: localizationProvider.translate({ type: 'Internal', text: 'columnSettings' }),
    actions: localizationProvider.translate({ type: 'Internal', text: 'actions' }),
    rowsPerPage: localizationProvider.translate({ type: 'Internal', text: 'rowsPerPage' }),
    of: localizationProvider.translate({ type: 'Internal', text: 'of' }),
    moreThan: localizationProvider.translate({ type: 'Internal', text: 'moreThan' }),
    noDataAvailable: localizationProvider.translate({ type: 'Internal', text: 'noDataAvailable' }),
    exportData: localizationProvider.translate({ type: 'Internal', text: 'exportData' }),
    exportFormat: localizationProvider.translate({ type: 'Internal', text: 'exportFormat' }),
    exportScope: localizationProvider.translate({ type: 'Internal', text: 'exportScope' }),
    currentPage: localizationProvider.translate({ type: 'Internal', text: 'currentPage' }),
    pageRange: localizationProvider.translate({ type: 'Internal', text: 'pageRange' }),
    allPages: localizationProvider.translate({ type: 'Internal', text: 'allPages' }),
    fromPage: localizationProvider.translate({ type: 'Internal', text: 'fromPage' }),
    toPage: localizationProvider.translate({ type: 'Internal', text: 'toPage' }),
    exportInfo: localizationProvider.translate({ type: 'Internal', text: 'exportInfo' }),
    cancel: localizationProvider.translate({ type: 'Internal', text: 'cancel' }),
  }), [localizationProvider]);

  // Handle export
  const handleExportClick = useCallback(() => {
    const totalPages = Math.ceil(processedData.length / rowsPerPage);
    setExportRangeEnd(totalPages);
    setExportDialogOpen(true);
  }, [processedData.length, rowsPerPage]);

  const handleExportClose = useCallback(() => {
    setExportDialogOpen(false);
  }, []);

  const handleExport = useCallback(() => {
    let dataToExport: any[] = [];

    // Determine which data to export based on scope
    switch (exportScope) {
      case 'current':
        dataToExport = paginatedData;
        break;
      case 'range':
        const startIndex = (exportRangeStart - 1) * rowsPerPage;
        const endIndex = exportRangeEnd * rowsPerPage;
        dataToExport = processedData.slice(startIndex, endIndex);
        break;
      case 'all':
      default:
        dataToExport = processedData;
        break;
    }

    // Prepare data with only visible columns
    const exportData = dataToExport.map(row => {
      const exportRow: any = {};
      visibleColumnsData.forEach(column => {
        exportRow[column.headerName] = getNestedValue(row, column.field);
      });
      return exportRow;
    });

    // Generate file based on format
    if (exportFormat === 'json') {
      // Export as JSON
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collection || 'export'}_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Export as CSV
      const headers = visibleColumnsData.map(col => col.headerName);
      const csvRows = [
        headers.join(','),
        ...exportData.map(row =>
          headers.map(header => {
            const value = row[header];
            if (value === null || value === undefined) return '';
            const stringValue = String(value);
            if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
              return `"${stringValue.replace(/"/g, '""')}"`;
            }
            return stringValue;
          }).join(',')
        )
      ];
      const csvString = csvRows.join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${collection || 'export'}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }

    setExportDialogOpen(false);
  }, [exportFormat, exportScope, exportRangeStart, exportRangeEnd, paginatedData, processedData, visibleColumnsData, rowsPerPage, collection, getNestedValue]);

  // Render table row
  const renderRow = useCallback((row: any) => {
    const isSelected = selectedRows.has(row.id);
    const isExpanded = expandedRows.has(row.id);

    return (
      <React.Fragment key={row.id}>
        <TableRow
          hover
          selected={isSelected}
          onClick={() => onRowClick && onRowClick(row)}
          sx={{
            cursor: onRowClick ? 'pointer' : 'default',
            '&:hover': {
              backgroundColor: theme.palette.action.hover,
            },
            ...(isSelected && {
              backgroundColor: theme.palette.action.selected,
            }),
          }}
        >
          {selectable && (
            <TableCell padding="checkbox">
              <Checkbox
                checked={isSelected}
                onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
              />
            </TableCell>
          )}

          {visibleColumnsData.map((column) => (
            <TableCell
              key={column.field}
              align={column.align || getRightOrLeft()}
              dir={getLtrOrRtlByAlignment(column.align || getRightOrLeft())}
              sx={{
                minWidth: column.minWidth || 120,
                maxWidth: column.maxWidth,
                ...column.sx
              }}
            >
              {column.render ? column.render(getNestedValue(row, column.field), row) : getNestedValue(row, column.field)}
            </TableCell>
          ))}

          {actions.length > 0 && (
            <TableCell
              align={getRightOrLeft()}
              dir={getRtlOrLtr()}
              sx={{ minWidth: 80 }}>
              <Stack direction="row" spacing={1}>
                {actions.map((action, actionIndex) => (
                  <Tooltip key={actionIndex} title={action.label && (typeof action.label === 'function' ? action.label(row) : action.label)}>
                    <span>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          action.onClick(row);
                        }}
                        disabled={action.disabled && action.disabled(row)}
                      >
                        {action.icon && (typeof action.icon === 'function' ? action.icon(row) : action.icon)}
                      </IconButton>
                    </span>
                  </Tooltip>
                ))}
              </Stack>
            </TableCell>
          )}

          {columns.some(col => col.expandable) && (
            <TableCell padding="none" sx={{ width: 48 }}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRowExpand(row.id);
                }}
              >
                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </TableCell>
          )}
        </TableRow>

        {columns.some(col => col.expandable) && (
          <TableRow>
            <TableCell
              colSpan={visibleColumnsData.length + (selectable ? 1 : 0) + (actions.length > 0 ? 1 : 0) + 1}
              sx={{ py: 0, border: 0 }}
            >
              <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <Box sx={{ py: 2 }}>
                  {columns
                    .filter(col => col.expandable)
                    .map(col => (
                      <Box key={col.field}>
                        {col.expandableRender?.(row)}
                      </Box>
                    ))
                  }
                </Box>
              </Collapse>
            </TableCell>
          </TableRow>
        )}
      </React.Fragment>
    );
  }, [
    selectedRows, expandedRows, visibleColumnsData, selectable, actions,
    columns, theme, onRowClick, handleRowSelect, handleRowExpand, getNestedValue
  ]);

  // Mobile card renderer
  const renderMobileCard = useCallback((row: any, index: number) => {
    const isSelected = selectedRows.has(row.id);

    return (
      <Card
        key={row.id}
        sx={{
          mb: 1,
          ...(isSelected && {
            borderColor: theme.palette.primary.main,
            backgroundColor: theme.palette.action.selected,
          })
        }}
        onClick={() => onRowClick && onRowClick(row)}
      >
        <CardContent sx={{ pb: '16px !important' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            {selectable && (
              <Checkbox
                checked={isSelected}
                onChange={(e) => handleRowSelect(row.id, e.target.checked)}
                onClick={(e) => e.stopPropagation()}
                sx={{ mr: 1 }}
              />
            )}
            <Typography variant="subtitle1" sx={{ flexGrow: 1 }}>
              {columns[0]?.render ? columns[0].render(getNestedValue(row, columns[0].field), row) : getNestedValue(row, columns[0]?.field)}
            </Typography>
            {actions.length > 0 && (
              <Stack direction="row" spacing={0.5}>
                {actions.slice(0, 4).map((action, actionIndex) => (
                  <IconButton
                    key={actionIndex}
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      action.onClick(row);
                    }}
                    disabled={action.disabled && action.disabled(row)}
                  >
                    {action.icon && (typeof action.icon === 'function' ? action.icon(row) : action.icon)}
                  </IconButton>
                ))}
              </Stack>
            )}
          </Box>

          {columns.slice(1, 4).map((column) => (
            <Box key={column.field} sx={{
              display: 'flex',
              mb: 0.5,
            }}>
              <Typography variant="body2" color="text.secondary" sx={{
                minWidth: 80,
              }}>
                {column.headerName}:
              </Typography>
              <Typography variant="body2" sx={{ [isRTL() ? 'mr' : 'ml']: 1 }}>
                {column.render ? column.render(getNestedValue(row, column.field), row) : getNestedValue(row, column.field)}
              </Typography>
            </Box>
          ))}
        </CardContent>
      </Card>
    );
  }, [selectedRows, theme, onRowClick, selectable, actions, columns, handleRowSelect, getNestedValue]);

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <IconButton color="inherit" size="small" onClick={refreshData}>
            <Refresh />
          </IconButton>
        }
      >
        {error}
      </Alert>
    );
  }

  const hasSelectedRows = selectedRows.size > 0;
  const isAllSelected = selectedRows.size === processedData.length && processedData.length > 0;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < processedData.length;

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', ...sx }} {...otherProps}>
      {/* Header */}
      <CardHeader
        title={localizationProvider.translate(title)}
        subheader={localizationProvider.translate(subtitle)}
        action={
          <Stack direction="row" spacing={1} alignItems="center">
            {/* Global Actions */}
            {globalActions.length > 0 && (
              <>
                {globalActions.map((action, index) => (
                  <Tooltip key={index} title={localizationProvider.translate(action.label)}>
                    <span>
                      <Button
                        variant={action.variant || 'contained'}
                        color={action.color || 'primary'}
                        size="small"
                        startIcon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        sx={{
                          minWidth: '120px',
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                          ...action.sx,
                          '& .MuiButton-startIcon': {
                            marginRight: 1,
                            marginLeft: 0
                          },
                          '[dir="rtl"] &': {
                            '& .MuiButton-startIcon': {
                              marginRight: 0,
                              marginLeft: 1
                            }
                          }
                        }}
                      >
                        {action.label}
                      </Button>
                    </span>
                  </Tooltip>
                ))}
              </>
            )}

            {/* Utility Actions */}
            {refreshable && (
              <Tooltip title={localizationProvider.translate(translatedStrings.refresh)}>
                <span>
                  <IconButton onClick={refreshData} disabled={loading}>
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            {exportable && (
              <Tooltip title={localizationProvider.translate(translatedStrings.export)}>
                <span>
                  <IconButton onClick={handleExportClick} disabled={processedData.length === 0}>
                    <GetApp />
                  </IconButton>
                </span>
              </Tooltip>
            )}

            <Tooltip title={localizationProvider.translate(translatedStrings.columnSettings)}>
              <span>
                <IconButton onClick={(e) => setColumnsAnchor(e.currentTarget)}>
                  <ViewColumn />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        }
      />

      {/* Toolbar */}
      <Toolbar
        sx={{
          px: 2,
          ...(hasSelectedRows && {
            bgcolor: theme.palette.action.selected,
          }),
        }}
      >
        {hasSelectedRows ? (
          <Typography variant="subtitle1" sx={{ flex: '1 1 100%' }}>
            {selectedRows.size} {translatedStrings.selected}
          </Typography>
        ) : (
          <>
            {searchable && (
              <FormControl variant="outlined" sx={{ minWidth: 200, mr: 2 }}>
                <OutlinedInput
                  size="small"
                  placeholder={translatedStrings.search}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  }
                />
              </FormControl>
            )}

            <Box sx={{ flexGrow: 1 }} />

            <Chip
              label={`${processedData.length} ${translatedStrings.items}`}
              variant="outlined"
              size="small"
            />
          </>
        )}
      </Toolbar>

      {loading && <LinearProgress />}

      {/* Table Content */}
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        {processedData.length === 0 && !loading ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 200,
              textAlign: 'center',
              p: 3
            }}
          >
            <Typography variant="body1" color="text.secondary">
              {emptyMessage || translatedStrings.noDataAvailable}
            </Typography>
          </Box>
        ) : isMobile ? (
          <Box sx={{ p: 2, maxHeight: maxHeight, overflow: 'auto' }}>
            {paginatedData.map((row, index) => renderMobileCard(row, index))}
          </Box>
        ) : (
          <TableContainer sx={{ flex: 1 }}>
            <Table stickyHeader={stickyHeader} size={dense ? 'small' : 'medium'}>
              <TableHead>
                <TableRow>
                  {selectable && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={isIndeterminate}
                        checked={isAllSelected}
                        onChange={(e) => handleSelectAll(e.target.checked)}
                      />
                    </TableCell>
                  )}

                  {visibleColumnsData.map((column) => (
                    <TableCell
                      key={column.field}
                      align={column.align || getRightOrLeft()}
                      sortDirection={sortConfig?.field === column.field ? sortConfig.direction : false}
                      sx={{
                        minWidth: column.minWidth || 120,
                        maxWidth: column.maxWidth,
                        fontWeight: 600,
                        ...column.headerSx
                      }}
                    >
                      {column.sortable !== false ? (
                        <TableSortLabel
                          active={sortConfig?.field === column.field}
                          direction={sortConfig?.field === column.field ? sortConfig.direction : 'asc'}
                          onClick={() => handleSort(column.field)}
                        >
                          {column.headerName}
                        </TableSortLabel>
                      ) : (
                        column.headerName
                      )}
                    </TableCell>
                  ))}

                  {actions.length > 0 && (
                    <TableCell align="right" sx={{ minWidth: 80 }}>
                      {translatedStrings.actions}
                    </TableCell>
                  )}

                  {columns.some(col => col.expandable) && (
                    <TableCell padding="none" sx={{ width: 48 }} />
                  )}
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedData.map((row) => renderRow(row))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Pagination */}
      {processedData.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50, 100]}
          component="div"
          count={processedData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={translatedStrings.rowsPerPage}
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} ${translatedStrings.of} ${count !== -1 ? count : `${translatedStrings.moreThan} ${to}`}`
          }
        />
      )}

      {/* Column visibility menu */}
      <Menu
        anchorEl={columnsAnchor}
        open={Boolean(columnsAnchor)}
        onClose={() => setColumnsAnchor(null)}
      >
        {columns.map((column) => (
          <MenuItem key={column.field}>
            <Checkbox
              checked={visibleColumns.has(column.field)}
              onChange={(e) => handleColumnVisibility(column.field, e.target.checked)}
            />
            {column.headerName}
          </MenuItem>
        ))}
      </Menu>

      {/* Export Dialog */}
      <Dialog
        open={exportDialogOpen}
        onClose={handleExportClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {translatedStrings.exportData}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            {/* Format Selection */}
            <FormControl component="fieldset">
              <FormLabel component="legend">
                {translatedStrings.exportFormat}
              </FormLabel>
              <RadioGroup
                value={exportFormat}
                onChange={(e) => setExportFormat(e.target.value as 'csv' | 'json')}
              >
                <FormControlLabel
                  value="csv"
                  control={<Radio />}
                  label="CSV"
                />
                <FormControlLabel
                  value="json"
                  control={<Radio />}
                  label="JSON"
                />
              </RadioGroup>
            </FormControl>

            {/* Scope Selection */}
            <FormControl component="fieldset">
              <FormLabel component="legend">
                {translatedStrings.exportScope}
              </FormLabel>
              <RadioGroup
                value={exportScope}
                onChange={(e) => setExportScope(e.target.value as 'current' | 'range' | 'all')}
              >
                <FormControlLabel
                  value="current"
                  control={<Radio />}
                  label={translatedStrings.currentPage}
                />
                <FormControlLabel
                  value="range"
                  control={<Radio />}
                  label={translatedStrings.pageRange}
                />
                <FormControlLabel
                  value="all"
                  control={<Radio />}
                  label={translatedStrings.allPages}
                />
              </RadioGroup>
            </FormControl>

            {/* Range inputs */}
            {exportScope === 'range' && (
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  label={translatedStrings.fromPage}
                  type="number"
                  value={exportRangeStart}
                  onChange={(e) => setExportRangeStart(Math.max(1, parseInt(e.target.value) || 1))}
                  inputProps={{
                    min: 1,
                    max: Math.ceil(processedData.length / rowsPerPage)
                  }}
                  size="small"
                  fullWidth
                />
                <Typography>-</Typography>
                <TextField
                  label={translatedStrings.toPage}
                  type="number"
                  value={exportRangeEnd}
                  onChange={(e) => setExportRangeEnd(Math.min(Math.ceil(processedData.length / rowsPerPage), parseInt(e.target.value) || 1))}
                  inputProps={{
                    min: exportRangeStart,
                    max: Math.ceil(processedData.length / rowsPerPage)
                  }}
                  size="small"
                  fullWidth
                />
              </Box>
            )}

            {/* Info about export */}
            <Box sx={{
              p: 2,
              bgcolor: 'action.hover',
              borderRadius: 1
            }}>
              <Typography variant="body2" color="text.secondary">
                {translatedStrings.exportInfo}:{' '}
                {exportScope === 'current' && `${paginatedData.length} ${translatedStrings.items}`}
                {exportScope === 'all' && `${processedData.length} ${translatedStrings.items}`}
                {exportScope === 'range' && `${Math.min(processedData.length, (exportRangeEnd - exportRangeStart + 1) * rowsPerPage)} ${translatedStrings.items}`}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportClose}>
            {translatedStrings.cancel}
          </Button>
          <Button
            onClick={handleExport}
            variant="contained"
            startIcon={<GetApp />}
          >
            {translatedStrings.export}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default MicrospecDataTable;
